import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from "react-toastify";
import { apiConnector } from "../apiConnector";
import { endpoint } from "../apis";
import Cookies from 'js-cookie';

export const fetchTotalAudience = createAsyncThunk(
    "audienceInsight/fetchTotalAudience",
    async ({ selectedChannelId, filterType, start_date, end_date }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                channel_id: selectedChannelId,
                filter_type: filterType
            };
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.totalAudience,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            if (error?.response?.data === "('Token has been expired',)") {
                Cookies.remove('jwt_token');
                Cookies.remove('authToken');
                window.location.href = '/';
                toast.error('Session expired. Please log in again.');
            } else {
                toast.error(error?.response?.data || 'Failed to fetch total audience data!');
            }
            return rejectWithValue(error?.response?.data)
        }
    }
);

export const fetchTotalSubscribers = createAsyncThunk(
    "audienceInsight/fetchTotalSubscribers",
    async ({ selectedChannelId, filterType, start_date, end_date }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                channel_id: selectedChannelId,
                filter_type: filterType
            };
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.newSubscribers,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            toast.error("Failed to fetch total subscribers data");
            return rejectWithValue("Failed to fetch total subscribers data");
        }
    }
);

export const fetchNetSubscribers = createAsyncThunk(
    "audienceInsight/fetchNetSubscribers",
    async ({ selectedChannelId, filterType, start_date, end_date }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                channel_id: selectedChannelId,
                filter_type: filterType
            };
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.netSubscribers,
                payload,
                {
                    Authorization: `Bearer ${token}`,
                }
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            toast.error("Failed to fetch net subscribers data");
            return rejectWithValue("Failed to fetch net subscribers data");
        }
    }
);

export const fetchSubscriberRate = createAsyncThunk(
    "audienceInsight/fetchSubscriberRate",
    async ({ selectedChannelId, filterType, start_date, end_date }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                channel_id: selectedChannelId,
                filter_type: filterType
            };
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.subscriberRate,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            toast.error("Failed to fetch subscriber rate data");
            return rejectWithValue("Failed to fetch subscriber rate data");
        }
    }
);

export const fetchAudienceRetention = createAsyncThunk(
    "audienceInsight/fetchAudienceRetention",
    async ({ selectedChannelId, filterType, start_date, end_date }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                channel_id: selectedChannelId,
                filter_type: filterType
            };
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.audienceRetention,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            toast.error("Failed to fetch audience retention data");
            return rejectWithValue("Failed to fetch audience retention data");
        }
    }
);

export const fetchAgeDistribution = createAsyncThunk(
    "audienceInsight/fetchAgeDistribution",
    async ({ selectedChannelId, filterType, selectedColumns, country_code, subscription_status, content_type, video_id, start_date, end_date }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                channel_id: selectedChannelId,
                filter_type: filterType
            };

            // Add country_code if provided
            if (country_code) {
                payload.country_code = country_code;
            }
            
            // Add subscription_status if provided
            if (subscription_status) {
                payload.subscription_status = subscription_status;
            }
            
            // Add content_type if provided
            if (content_type) {
                payload.live_or_on_demand = content_type;
            }
            
            // Add video_id if provided
            if (video_id) {
                payload.video_id = video_id;
            }
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            // Add selectedColumns (empty array if not provided)
            payload.selected_columns = (selectedColumns && selectedColumns.length > 0) ? selectedColumns : [];
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.ageDistribution,
                payload,
                {
                    Authorization: `Bearer ${token}`,
                }
            );
            
            // Handle new nested response format: {Response: {age_distribution: []}}
            // Also support old flat format: {Response: []} for backward compatibility
            const responseData = response.Response?.age_distribution || 
                                 response.data?.Response?.age_distribution || 
                                 response.Response || 
                                 response.data?.Response || 
                                 [];
            
            return responseData;
        } catch (error) {
            console.error('Age Distribution API - Error:', error);
            console.error('Age Distribution API - Error response:', error.response);
            toast.error("Failed to fetch age distribution data");
            return rejectWithValue("Failed to fetch age distribution data");
        }
    }
);

// Simplified CSV download function
const createCSVDownloadThunk = (actionName, apiEndpoint, dataType) => createAsyncThunk(
    actionName,
    async ({ selectedChannelId, filterType }, { rejectWithValue }) => {
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            const payload = {
                channel_id: selectedChannelId,
                filter_type: filterType,
                export_csv: true
            };

            const response = await apiConnector(
                "POST",
                apiEndpoint,
                payload,
                { Authorization: `Bearer ${token}` }
            );

            let csvData = response.data;
            
            // Check for empty data
            if (typeof csvData === 'object' && csvData.Response && Array.isArray(csvData.Response) && csvData.Response.length === 0) {
                toast.info(`No ${dataType} data available to download`);
                return rejectWithValue("No data available");
            }
            
            if (typeof csvData === 'string') {
                const lines = csvData.trim().split('\n');
                if (lines.length <= 1) {
                    toast.info(`No ${dataType} data available to download`);
                    return rejectWithValue("No data available");
                }
            }

            // Create and download CSV file
            const blob = new Blob([csvData], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${dataType.replace(' ', '_')}_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success(`${dataType} CSV downloaded successfully`);
            return { success: true, dataType };
        } catch (error) {
            console.error(`${dataType} CSV Download Error:`, error);
            toast.error(`Failed to download ${dataType} CSV`);
            return rejectWithValue(`Failed to download ${dataType} CSV`);
        }
    }
);

// Create specific download functions
export const downloadAgeDistributionCSV = createCSVDownloadThunk(
    "audienceInsight/downloadAgeDistributionCSV",
    endpoint.audience.ageDistribution,
    "age distribution"
);

export const downloadGenderDistributionCSV = createCSVDownloadThunk(
    "audienceInsight/downloadGenderDistributionCSV", 
    endpoint.audience.genderDistribution,
    "gender distribution"
);

export const downloadTopCountriesCSV = createCSVDownloadThunk(
    "audienceInsight/downloadTopCountriesCSV",
    endpoint.audience.topCountries,
    "top countries"
);

export const downloadDeviceDistributionCSV = createCSVDownloadThunk(
    "audienceInsight/downloadDeviceDistributionCSV",
    endpoint.audience.deviceDistribution,
    "device distribution"
);

export const downloadInterestCategoriesCSV = createCSVDownloadThunk(
    "audienceInsight/downloadInterestCategoriesCSV",
    endpoint.audience.interestCategories,
    "interests categories"
);

export const downloadTrafficSourcesCSV = createCSVDownloadThunk(
    "audienceInsight/downloadTrafficSourcesCSV",
    endpoint.audience.trafficSources,
    "traffic sources"
);

export const downloadSharesDistributionCSV = createCSVDownloadThunk(
    "audienceInsight/downloadSharesDistributionCSV",
    endpoint.audience.sharesDistribution,
    "shares distribution"
);

export const downloadPremiumViewsCSV = createCSVDownloadThunk(
    "audienceInsight/downloadPremiumViewsCSV",
    endpoint.audience.premiumViews,
    "premium views"
);

export const downloadCardPerformanceCSV = createCSVDownloadThunk(
    "audienceInsight/downloadCardPerformanceCSV",
    endpoint.audience.cardPerformance,
    "card performance"
);

export const fetchGenderDistribution = createAsyncThunk(
    "audienceInsight/fetchGenderDistribution",
    async ({ selectedChannelId, filterType, selectedColumns, country_code, video_id, start_date, end_date, subscription_status, content_type }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                channel_id: selectedChannelId,
                filter_type: filterType
            };

            // Add country_code if provided
            if (country_code) {
                payload.country_code = country_code;
            }   
            
            // Add video_id if provided
            if (video_id) {
                payload.video_id = video_id;
            }
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            // Add subscription_status if provided
            if (subscription_status) {
                payload.subscription_status = subscription_status;
            }
            
            // Add content_type if provided
            if (content_type) {
                payload.live_or_on_demand = content_type;
            }
            
            // Add selectedColumns (empty array if not provided)
            payload.selected_columns = (selectedColumns && selectedColumns.length > 0) ? selectedColumns : [];
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.genderDistribution,
                payload,
                {
                    Authorization: `Bearer ${token}`,
                }
            );

            // Handle new nested response format: {Response: {gender_distribution: []}}
            // Also support old flat format: {Response: []} for backward compatibility
            const data = response.Response?.gender_distribution || 
                         response.data?.Response?.gender_distribution || 
                         response.Response || 
                         response.data?.Response || 
                         [];
            return data;
        } catch (error) {
            toast.error("Failed to fetch gender distribution data");
            return rejectWithValue("Failed to fetch gender distribution data");
        }
    }
);

export const fetchTopCountries = createAsyncThunk(
    "audienceInsight/fetchTopCountries",
    async ({ selectedChannelId, filterType, selectedColumns, country_code, video_id, start_date, end_date, subscription_status, content_type }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                channel_id: selectedChannelId,
                filter_type: filterType
            };

            // Add country_code if provided
            if (country_code) {
                payload.country_code = country_code;
            }   
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            // Add subscription_status if provided
            if (subscription_status) {
                payload.subscription_status = subscription_status;
            }   
            
            // Add content_type if provided
            if (content_type) {
                payload.live_or_on_demand = content_type;
            }

            // Add video_id if provided
            if (video_id) {
                payload.video_id = video_id;
            }
            
            // Add selectedColumns (empty array if not provided)
            payload.selected_columns = (selectedColumns && selectedColumns.length > 0) ? selectedColumns : []; 
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.topCountries,
                payload,
                {
                    Authorization: `Bearer ${token}`,
                }
            );

            // Handle new nested response format: {Response: {top_countries: []}}
            // Also support old flat format: {Response: []} for backward compatibility
            const data = response.Response?.top_countries || 
                         response.data?.Response?.top_countries || 
                         response.Response || 
                         response.data?.Response || 
                         [];
            return data;
        } catch (error) {
            toast.error("Failed to fetch top countries data");
            return rejectWithValue("Failed to fetch top countries data");
        }
    }
);

export const fetchDeviceDistribution = createAsyncThunk(
    "audienceInsight/fetchDeviceDistribution",
    async ({ selectedChannelId, filterType, selectedColumns,country_code, video_id, start_date, end_date, subscription_status, content_type }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                channel_id: selectedChannelId,
                filter_type: filterType
            };

            // Add country_code if provided
            if (country_code) {
                payload.country_code = country_code;
            }   
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            // Add subscription_status if provided
            if (subscription_status) {
                payload.subscription_status = subscription_status;
            }
            
            // Add content_type if provided
            if (content_type) {
                payload.live_or_on_demand = content_type;
            }
            
            // Add video_id if provided
            if (video_id) {
                payload.video_id = video_id;
            }
            
            // Add selectedColumns (empty array if not provided)
            payload.selected_columns = (selectedColumns && selectedColumns.length > 0) ? selectedColumns : [];
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.deviceDistribution,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            if (error?.response?.data === "('Token has been expired',)") {
                Cookies.remove('jwt_token');
                Cookies.remove('authToken');
                window.location.href = '/';
                toast.error('Session expired. Please log in again.');
            } else {
                toast.error(error?.response?.data || 'Failed to fetch device distribution data!');
            }
            return rejectWithValue(error?.response?.data)
        }
    }
);

export const fetchInterestCategories = createAsyncThunk(
    "audienceInsight/fetchInterestCategories",
    async ({ selectedChannelId, filterType, selectedColumns,country_code,video_id, start_date, end_date, subscription_status, content_type }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                channel_id: selectedChannelId,
                filter_type: filterType
            };

            // Add video_id if provided
            if (video_id) {
                payload.video_id = video_id;
            }

            // Add country_code if provided
            if (country_code) {
                payload.country_code = country_code;
            }   
            
            // Add subscription_status if provided
            if (subscription_status) {
                payload.subscription_status = subscription_status;
            }
            
            // Add content_type if provided
            if (content_type) {
                payload.live_or_on_demand = content_type;
            }
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            // Add selectedColumns (empty array if not provided)
            payload.selected_columns = (selectedColumns && selectedColumns.length > 0) ? selectedColumns : [];
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.interestCategories,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            if (error?.response?.data === "('Token has been expired',)") {
                Cookies.remove('jwt_token');
                Cookies.remove('authToken');
                window.location.href = '/';
                toast.error('Session expired. Please log in again.');
            } else {
                toast.error(error?.response?.data || 'Failed to fetch interest categories data!');
            }
            return rejectWithValue(error?.response?.data)
        }
    }
);

export const fetchTrafficSources = createAsyncThunk(
    "audienceInsight/fetchTrafficSources",
    async ({ selectedChannelId, filterType, selectedColumns,country_code, video_id, start_date, end_date, subscription_status, content_type }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                channel_id: selectedChannelId,
                filter_type: filterType
            };

            // Add country_code if provided
            if (country_code) {
                payload.country_code = country_code;
            }   
            
            // Add video_id if provided
            if (video_id) {
                payload.video_id = video_id;
            }
            
            // Add subscription_status if provided
            if (subscription_status) {
                payload.subscription_status = subscription_status;
            }
            
            // Add content_type if provided
            if (content_type) {
                payload.live_or_on_demand = content_type;
            }
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            // Add selectedColumns (empty array if not provided)
            payload.selected_columns = (selectedColumns && selectedColumns.length > 0) ? selectedColumns : [];
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.trafficSources,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            if (error?.response?.data === "('Token has been expired',)") {
                Cookies.remove('jwt_token');
                Cookies.remove('authToken');
                window.location.href = '/';
                toast.error('Session expired. Please log in again.');
            } else {
                toast.error(error?.response?.data || 'Failed to fetch traffic sources data!');
            }
            return rejectWithValue(error?.response?.data)
        }
    }
);

export const fetchSharesDistribution = createAsyncThunk(
    "audienceInsight/fetchSharesDistribution",
    async ({ selectedChannelId, filterType, selectedColumns,country_code, video_id, start_date, end_date, subscription_status, content_type }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                channel_id: selectedChannelId,
                filter_type: filterType
            };

            // Add country_code if provided
            if (country_code) {
                payload.country_code = country_code;
            }   
            
            // Add video_id if provided
            if (video_id) {
                payload.video_id = video_id;
            }
            
            // Add subscription_status if provided
            if (subscription_status) {
                payload.subscription_status = subscription_status;
            }
            
            // Add content_type if provided
            if (content_type) {
                payload.live_or_on_demand = content_type;
            }
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            // Add selectedColumns (empty array if not provided)
            payload.selected_columns = (selectedColumns && selectedColumns.length > 0) ? selectedColumns : [];
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.sharesDistribution,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            if (error?.response?.data === "('Token has been expired',)") {
                Cookies.remove('jwt_token');
                Cookies.remove('authToken');
                window.location.href = '/';
                toast.error('Session expired. Please log in again.');
            } else {
                toast.error(error?.response?.data || 'Failed to fetch shares distribution data!');
            }
            return rejectWithValue(error?.response?.data)
        }
    }
);

export const fetchPremiumViews = createAsyncThunk(
    "audienceInsight/fetchPremiumViews",
    async ({ selectedChannelId, filterType, selectedColumns,country_code, video_id, start_date, end_date, subscription_status, content_type }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                channel_id: selectedChannelId,
                filter_type: filterType
            };
            
            // Add country_code if provided
            if (country_code) {
                payload.country_code = country_code;
            }   
            
            // Add video_id if provided
            if (video_id) {
                payload.video_id = video_id;
            }
            
            // Add subscription_status if provided
            if (subscription_status) {
                payload.subscription_status = subscription_status;
            }
            
            // Add content_type if provided
            if (content_type) {
                payload.live_or_on_demand = content_type;
            }
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            // Add selectedColumns (empty array if not provided)
            payload.selected_columns = (selectedColumns && selectedColumns.length > 0) ? selectedColumns : [];
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.premiumViews,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            if (error?.response?.data === "('Token has been expired',)") {
                Cookies.remove('jwt_token');
                Cookies.remove('authToken');
                window.location.href = '/';
                toast.error('Session expired. Please log in again.');
            } else {
                toast.error(error?.response?.data || 'Failed to fetch premium views data!');
            }
            return rejectWithValue(error?.response?.data)
        }
    }
);

export const fetchVideoList = createAsyncThunk(
    "audienceInsight/fetchVideoList",
    async ({ selectedChannelId }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                channel_id: selectedChannelId
            };
            
            const response = await apiConnector(
                "GET",
                endpoint.audience.videoList,
                payload,
                {
                    Authorization: `Bearer ${token}`,
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error fetching video list:", error);
            return rejectWithValue(error.response?.data || "Failed to fetch video list");
        }
    }
);

export const fetchCardPerformance = createAsyncThunk(
    "audienceInsight/fetchCardPerformance",
    async ({ selectedChannelId, filterType, selectedColumns,country_code, video_id, start_date, end_date, subscription_status, content_type }, { rejectWithValue }) => {
        // Get token from cookies
        const token = Cookies.get('jwt_token') || Cookies.get('authToken');

        try {
            // Create request payload
            const payload = {
                channel_id: selectedChannelId,
                filter_type: filterType
            };

            // Add video_id if provided
            if (video_id) {
                payload.video_id = video_id;
            }

            // Add subscription_status if provided
            if (subscription_status) {
                payload.subscription_status = subscription_status;
            }
            
            // Add content_type if provided
            if (content_type) {
                payload.live_or_on_demand = content_type;
            }
            
            // Add country_code if provided
            if (country_code) {
                payload.country_code = country_code;
            }   
            
            // Add start_date and end_date if filterType is custom
            if (filterType === 'custom' && start_date && end_date) {
                payload.start_date = start_date;
                payload.end_date = end_date;
            }
            
            // Add selectedColumns (empty array if not provided)
            payload.selected_columns = (selectedColumns && selectedColumns.length > 0) ? selectedColumns : [];
            
            const response = await apiConnector(
                "POST",
                endpoint.audience.cardPerformance,
                payload,
                { Authorization: `Bearer ${token}` },
                null,
            );

            // Handle different response formats
            const data = response.Response || response.data?.Response || [];
            return data;
        } catch (error) {
            if (error?.response?.data === "('Token has been expired',)") {
                Cookies.remove('jwt_token');
                Cookies.remove('authToken');
                window.location.href = '/';
                toast.error('Session expired. Please log in again.');
            } else {
                toast.error(error?.response?.data || 'Failed to fetch card performance data!');
            }
            return rejectWithValue(error?.response?.data)
        }
    }
);






